// React
import PropTypes from "prop-types";
import React, { Suspense, useMemo, useState } from "react";
// Helpers
import { isObject } from "@mefisto/utils";
// Framework
import { classnames } from "ui/classnames";
import { makeStyles, ModalBackdrop } from "ui/components";
// Icons
import { PiImageDuotone as ImageIcon } from "@mefisto/icon";
// Components
import ErrorBoundary from "./components/ErrorBoundary";
import ImageComponent from "./components/ImageComponent";
import Loader from "./components/Loader";

////////////////////////////////////////////////////
/// Styles
////////////////////////////////////////////////////

const useStyles = makeStyles((theme) => ({
  root: ({ width, height, maxWidth, maxHeight }) => ({
    position: "relative",
    overflow: "hidden",
    display: "flex",
    width,
    height,
    maxWidth,
    maxHeight,
  }),
  image: ({ width, height, maxWidth, maxHeight, objectFit }) => ({
    width,
    height,
    maxWidth,
    maxHeight,
    objectFit,
    borderRadius: "inherit",
  }),
  fallback: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    color: theme.palette.border.prominent.color,
    backgroundColor: theme.palette.background.surface.main,
    border: theme.palette.border.surface.main,
  },
  optimizeContrast: {
    imageRendering: "-webkit-optimize-contrast",
  },
  variantRounded: {
    borderRadius: theme.radius.small,
  },
  variantCircle: {
    borderRadius: theme.radius.rounded,
  },
}));

////////////////////////////////////////////////////
/// Component
////////////////////////////////////////////////////

const Image = ({
  image,
  width = "100%",
  height = "100%",
  objectFit = "cover",
  maxWidth,
  maxHeight,
  fallback,
  variant = "default",
  optimizeContrast,
  loader = <Loader />,
  containerProps,
  imageProps,
  draggable = false,
  onClick,
}) => {
  // Styles
  const classes = useStyles({ width, height, maxWidth, maxHeight, objectFit });
  // State
  const [blob, setBlob] = useState(null);
  const [backdrop, setBackdrop] = useState(false);
  // Memo
  const url = useMemo(() => {
    if (isObject(image)) {
      if (!image.url) {
        return null;
      }
      const url = new URL(image.url);
      if (url.protocol === "blob:") {
        setBackdrop(true);
        setBlob(url.toString());
      } else {
        setBackdrop(false);
      }
      if (blob) {
        return blob;
      }
      return url.toString();
    }
    return image;
  }, [image, blob]);
  // Render
  return (
    <div
      className={classnames(classes.root, {
        [classes.variantRounded]: variant === "rounded",
        [classes.variantCircle]: variant === "circle",
      })}
      {...containerProps}
    >
      <ErrorBoundary>
        <Suspense fallback={loader}>
          <ModalBackdrop open={backdrop} />
          {url ? (
            <ImageComponent
              srcList={url}
              draggable={draggable}
              className={classnames(classes.image, {
                [classes.optimizeContrast]: optimizeContrast,
              })}
              onClick={onClick}
              {...imageProps}
            />
          ) : (
            fallback ?? (
              <div
                className={classnames(classes.fallback, {
                  [classes.variantRounded]: variant === "rounded",
                  [classes.variantCircle]: variant === "circle",
                })}
              >
                <ImageIcon />
              </div>
            )
          )}
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};

Image.propTypes = {
  image: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      generation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      url: PropTypes.string,
    }),
  ]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  objectFit: PropTypes.string,
  variant: PropTypes.oneOf(["default", "rounded", "circle"]),
  optimizeContrast: PropTypes.bool,
  draggable: PropTypes.bool,
  loader: PropTypes.element,
  fallback: PropTypes.oneOfType([PropTypes.element, PropTypes.oneOf([false])]),
};

export default Image;
